import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Plus, Trash2, Settings, Globe, Filter, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { RISK_CATEGORIES_LABELS } from '@/lib/utils';
import { useUserProfile } from '@/lib/useUserProfile';
import { NavBar } from '@/components/NavBar';

const COUNTRIES = [
  { code: 'CN', name: 'China' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'PH', name: 'Philippines' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'IN', name: 'India' },
  { code: 'JP', name: 'Japan' },
  { code: 'TW', name: 'Taiwan' }
];

const TRIGGER_TYPE_LABELS = {
  country_risk: 'Country risk score',
  freight_rate: 'Avg freight rate (USD/TEU)',
  port_wait: 'Max port wait (days)',
};

const AlertsPage = () => {
  const navigate = useNavigate();
  const { monitoredCountries: userCountries, audienceType } = useUserProfile();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeView, setActiveView] = useState('feed');
  const [newAlert, setNewAlert] = useState({
    alert_name: '',
    trigger_condition: '',
    countries: userCountries.length > 0 ? userCountries.slice(0, 3) : [],
    risk_categories: [],
    threshold: 'moderate',
    notification_method: 'both',
    trigger_type: 'none',
    trigger_target: '',
    trigger_operator: 'above',
    trigger_value: ''
  });

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await api.getAlerts(audienceType);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [audienceType]);

  const fetchTriggeredAlerts = useCallback(async () => {
    try {
      const response = await api.getTriggeredAlerts();
      setTriggeredAlerts(response.data || []);
    } catch (error) {
      console.error('Error fetching triggered alerts:', error);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchTriggeredAlerts();
  }, [fetchAlerts, fetchTriggeredAlerts]);

  const handleToggleAlert = async (alertId, currentStatus) => {
    try {
      await api.updateAlert(alertId, !currentStatus);
      setAlerts(alerts.map(a => 
        a.id === alertId ? { ...a, is_active: !currentStatus } : a
      ));
      toast.success(`Alert ${!currentStatus ? 'activated' : 'paused'}`);
    } catch (error) {
      console.error('Error updating alert:', error);
      toast.error('Failed to update alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await api.deleteAlert(alertId);
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
  };

  const handleCreateAlert = async () => {
    if (!newAlert.alert_name || newAlert.countries.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = { ...newAlert };
      if (!payload.trigger_type || payload.trigger_type === 'none') {
        delete payload.trigger_type;
        delete payload.trigger_target;
        delete payload.trigger_operator;
        delete payload.trigger_value;
      } else {
        payload.trigger_value = parseFloat(payload.trigger_value);
        if (Number.isNaN(payload.trigger_value)) {
          toast.error('Enter a numeric threshold value for the custom trigger');
          return;
        }
        if (!payload.trigger_target) {
          toast.error('Select a target country for the custom trigger');
          return;
        }
      }
      const response = await api.createAlert(payload);
      setAlerts([...alerts, response.data]);
      setDialogOpen(false);
      setNewAlert({
        alert_name: '',
        trigger_condition: '',
        countries: [],
        risk_categories: [],
        threshold: 'moderate',
        notification_method: 'both',
        trigger_type: 'none',
        trigger_target: '',
        trigger_operator: 'above',
        trigger_value: ''
      });
      toast.success('Alert created successfully');
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error('Failed to create alert');
    }
  };

  const toggleCountrySelection = (code) => {
    setNewAlert(prev => ({
      ...prev,
      countries: prev.countries.includes(code)
        ? prev.countries.filter(c => c !== code)
        : [...prev.countries, code]
    }));
  };

  const toggleCategorySelection = (category) => {
    setNewAlert(prev => ({
      ...prev,
      risk_categories: prev.risk_categories.includes(category)
        ? prev.risk_categories.filter(c => c !== category)
        : [...prev.risk_categories, category]
    }));
  };

  const getThresholdBadge = (threshold) => {
    switch (threshold) {
      case 'critical':
        return <Badge className="bg-[#FF3B30] text-white">Critical only</Badge>;
      case 'significant':
        return <Badge className="bg-[#FF9500] text-white">Significant</Badge>;
      case 'moderate':
        return <Badge className="bg-[#00707C] text-white">Moderate</Badge>;
      default:
        return <Badge className="bg-[#4E6974] text-white">Any change</Badge>;
    }
  };

  const getNotificationBadge = (method) => {
    switch (method) {
      case 'email':
        return <Badge variant="outline" className="border-[#0097A7] text-[#0097A7]">Email</Badge>;
      case 'in-app':
        return <Badge variant="outline" className="border-[#0097A7] text-[#0097A7]">In-app</Badge>;
      default:
        return <Badge variant="outline" className="border-[#0097A7] text-[#0097A7]">Both</Badge>;
    }
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };


  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Navigation */}
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 mb-8 flex items-center justify-between"
          >
            <div>
              <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="alerts-title">
                Alert configuration
              </h1>
              <p className="text-[#4E6974] mt-2">
                Manage your risk alert rules and notification preferences
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#00707C] hover:bg-[#00707C]/90 text-white"
                  data-testid="create-alert-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create new alert
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#0D3B4F]">Create new alert</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="alert_name">Alert name</Label>
                    <Input
                      id="alert_name"
                      value={newAlert.alert_name}
                      onChange={(e) => setNewAlert({ ...newAlert, alert_name: e.target.value })}
                      placeholder="e.g., China Critical Risk Alert"
                      className="mt-1"
                      data-testid="alert-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Countries</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {COUNTRIES.map(country => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => toggleCountrySelection(country.code)}
                          className={`px-3 py-1 rounded-full text-sm transition-colors ${
                            newAlert.countries.includes(country.code)
                              ? 'bg-[#00707C] text-white'
                              : 'bg-[#F5F5F5] text-[#4E6974] hover:bg-[#E0E0E0]'
                          }`}
                          data-testid={`alert-country-${country.code.toLowerCase()}`}
                        >
                          {country.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Risk categories</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(RISK_CATEGORIES_LABELS).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleCategorySelection(key)}
                          className={`px-3 py-1 rounded-full text-xs transition-colors ${
                            newAlert.risk_categories.includes(key)
                              ? 'bg-[#00707C] text-white'
                              : 'bg-[#F5F5F5] text-[#4E6974] hover:bg-[#E0E0E0]'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Threshold</Label>
                    <Select 
                      value={newAlert.threshold}
                      onValueChange={(value) => setNewAlert({ ...newAlert, threshold: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="alert-threshold-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any change</SelectItem>
                        <SelectItem value="moderate">Moderate changes</SelectItem>
                        <SelectItem value="significant">Significant changes</SelectItem>
                        <SelectItem value="critical">Critical only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border border-[#E0E0E0] rounded-lg p-3 space-y-3" data-testid="custom-trigger-section">
                    <div>
                      <Label>Custom trigger (optional)</Label>
                      <p className="text-xs text-[#4E6974] mt-0.5">Get notified when a live metric crosses your own threshold.</p>
                    </div>
                    <Select
                      value={newAlert.trigger_type}
                      onValueChange={(value) => setNewAlert({ ...newAlert, trigger_type: value })}
                    >
                      <SelectTrigger data-testid="trigger-type-select">
                        <SelectValue placeholder="No custom trigger" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No custom trigger</SelectItem>
                        <SelectItem value="country_risk">Country risk score (0-100)</SelectItem>
                        <SelectItem value="freight_rate">Avg freight rate (USD/TEU)</SelectItem>
                        <SelectItem value="port_wait">Max port wait (days)</SelectItem>
                      </SelectContent>
                    </Select>
                    {newAlert.trigger_type && newAlert.trigger_type !== 'none' && (
                      <div className="grid grid-cols-3 gap-2">
                        <Select
                          value={newAlert.trigger_target}
                          onValueChange={(value) => setNewAlert({ ...newAlert, trigger_target: value })}
                        >
                          <SelectTrigger data-testid="trigger-target-select">
                            <SelectValue placeholder="Country" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={newAlert.trigger_operator}
                          onValueChange={(value) => setNewAlert({ ...newAlert, trigger_operator: value })}
                        >
                          <SelectTrigger data-testid="trigger-operator-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="above">Above</SelectItem>
                            <SelectItem value="below">Below</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={newAlert.trigger_value}
                          onChange={(e) => setNewAlert({ ...newAlert, trigger_value: e.target.value })}
                          placeholder={newAlert.trigger_type === 'country_risk' ? 'e.g. 70' : newAlert.trigger_type === 'freight_rate' ? 'e.g. 2500' : 'e.g. 4'}
                          data-testid="trigger-value-input"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Notification method</Label>
                    <Select 
                      value={newAlert.notification_method}
                      onValueChange={(value) => setNewAlert({ ...newAlert, notification_method: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="alert-notification-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in-app">In-app only</SelectItem>
                        <SelectItem value="email">Email only</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="trigger_condition">Trigger condition</Label>
                    <Input
                      id="trigger_condition"
                      value={newAlert.trigger_condition}
                      onChange={(e) => setNewAlert({ ...newAlert, trigger_condition: e.target.value })}
                      placeholder="e.g., Risk score exceeds 75"
                      className="mt-1"
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="trigger-presets">
                      {[
                        'Port wait time exceeds 3 days',
                        'Port status changes to Congested or Disrupted',
                        'Freight rate increases by more than 10%',
                        'Chokepoint status changes',
                        'Weather event affecting my ports',
                        'Risk score exceeds 75',
                      ].map(preset => (
                        <button key={preset} type="button" onClick={() => setNewAlert({ ...newAlert, trigger_condition: preset })}
                          className="px-2 py-1 rounded text-[10px] bg-[#F5F5F5] text-[#4E6974] hover:bg-[#E0E0E0] transition-colors">
                          {preset}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateAlert}
                    className="bg-[#00707C] hover:bg-[#00707C]/90 text-white"
                    data-testid="save-alert-btn"
                  >
                    Create alert
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </motion.div>

          {/* Tab toggle: Feed vs Configuration */}
          <div className="flex gap-2 mb-6">
            <Button variant={activeView === 'feed' ? 'default' : 'outline'} size="sm"
              className={activeView === 'feed' ? 'bg-[#00707C] hover:bg-[#00707C]/90' : ''}
              onClick={() => setActiveView('feed')} data-testid="view-feed">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> Recent triggered alerts
            </Button>
            <Button variant={activeView === 'config' ? 'default' : 'outline'} size="sm"
              className={activeView === 'config' ? 'bg-[#00707C] hover:bg-[#00707C]/90' : ''}
              onClick={() => setActiveView('config')} data-testid="view-config">
              <Settings className="h-4 w-4 mr-1.5" /> Alert configuration ({alerts.length})
            </Button>
          </div>

          {/* Triggered Alerts Feed */}
          {activeView === 'feed' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
                <CardContent className="p-0">
                  {triggeredAlerts.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="h-12 w-12 text-[#2E7D32] mx-auto mb-4" />
                      <p className="text-[#4E6974]">No recent alert triggers</p>
                      <p className="text-sm text-[#4E6974] mt-1">All monitored conditions are within normal thresholds</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#E0E0E0]">
                      {triggeredAlerts.map((ta, i) => {
                        const sevColor = ta.severity === 'CRITICAL' ? '#FF3B30' : ta.severity === 'HIGH' ? '#FF9500' : ta.severity === 'MEDIUM' ? '#FFCC02' : '#4E6974';
                        const timeAgo = getTimeAgo(ta.triggered_at);
                        return (
                          <div key={ta.id || i} className="flex items-start gap-4 p-4 hover:bg-[#F5F5F5]/50 transition-colors cursor-pointer"
                            onClick={() => ta.country_code ? navigate(`/countries/${ta.country_code}`) : null}
                            data-testid={`triggered-alert-${i}`}>
                            <div className="w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: sevColor }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-[#0D3B4F]">{ta.alert_name}</span>
                                <Badge style={{ backgroundColor: sevColor + '15', color: sevColor }} className="text-[10px]">{ta.severity}</Badge>
                                {ta.country_name && <Badge variant="secondary" className="bg-[#F5F5F5] text-[#4E6974] text-[10px]">{ta.country_name}</Badge>}
                              </div>
                              <p className="text-sm text-[#4E6974] leading-relaxed">{ta.summary}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-[10px] text-[#4E6974] flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo}</span>
                                <span className="text-[10px] text-[#4E6974]">Source: {ta.source}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Alerts Configuration Table */}
          {activeView === 'config' && (
          <>
          {/* Alerts Table */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardContent className="p-0">
                {loading ? (
                  <div className="text-center py-12 text-[#4E6974]">Loading alerts...</div>
                ) : alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="h-12 w-12 text-[#4E6974] mx-auto mb-4" />
                    <p className="text-[#4E6974]">No alerts configured yet</p>
                    <p className="text-sm text-[#4E6974] mt-1">Create your first alert to get started</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5F5]">
                        <TableHead className="text-[#0D3B4F] font-semibold">Alert name</TableHead>
                        <TableHead className="text-[#0D3B4F] font-semibold">Trigger condition</TableHead>
                        <TableHead className="text-[#0D3B4F] font-semibold">Countries</TableHead>
                        <TableHead className="text-[#0D3B4F] font-semibold">Threshold</TableHead>
                        <TableHead className="text-[#0D3B4F] font-semibold">Notification</TableHead>
                        <TableHead className="text-[#0D3B4F] font-semibold">Status</TableHead>
                        <TableHead className="text-[#0D3B4F] font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alerts.map((alert, index) => (
                        <TableRow 
                          key={alert.id}
                          className="hover:bg-[#F5F5F5]/50"
                          data-testid={`alert-row-${index}`}
                        >
                          <TableCell className="font-medium text-[#0D3B4F]">
                            {alert.alert_name}
                          </TableCell>
                          <TableCell className="text-[#4E6974] text-sm max-w-[220px]">
                            {alert.trigger_type && alert.trigger_type !== 'none' ? (
                              <Badge variant="outline" className="border-[#0097A7] text-[#0097A7] text-xs font-normal whitespace-nowrap" data-testid={`custom-rule-${index}`}>
                                {TRIGGER_TYPE_LABELS[alert.trigger_type] || alert.trigger_type} {alert.trigger_operator === 'below' ? '<' : '>'} {alert.trigger_value} ({alert.trigger_target})
                              </Badge>
                            ) : (
                              <span className="block truncate">{alert.trigger_condition || '-'}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {alert.countries?.slice(0, 3).map(code => (
                                <Badge key={code} variant="secondary" className="bg-[#F5F5F5] text-[#4E6974] text-xs">
                                  {code}
                                </Badge>
                              ))}
                              {alert.countries?.length > 3 && (
                                <Badge variant="secondary" className="bg-[#F5F5F5] text-[#4E6974] text-xs">
                                  +{alert.countries.length - 3}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getThresholdBadge(alert.threshold)}
                          </TableCell>
                          <TableCell>
                            {getNotificationBadge(alert.notification_method)}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={alert.is_active}
                              onCheckedChange={() => handleToggleAlert(alert.id, alert.is_active)}
                              data-testid={`alert-toggle-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAlert(alert.id)}
                              className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              data-testid={`alert-delete-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </motion.div>
          </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AlertsPage;
